import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { UserWithContext as User } from "@platform/types";

/**
 * Refresh token is kept in module-level memory only — NOT persisted to
 * localStorage. This means it is lost on page reload (the user re-logs in via
 * the access token still in storage, or sees the login page if that too has
 * expired). This is an intentional security trade-off.
 *
 * The access token (15 min TTL) is persisted so that page reloads within an
 * active session feel seamless.
 */
let _refreshToken: string | null = null;

export function getRefreshToken(): string | null {
  return _refreshToken;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  setSession: (session: { user: User; accessToken: string; refreshToken: string }) => void;
  setTokens: (tokens: { accessToken: string; refreshToken: string }) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,

      setSession: (session) => {
        _refreshToken = session.refreshToken;
        set({ user: session.user, accessToken: session.accessToken });
      },

      setTokens: (tokens) => {
        _refreshToken = tokens.refreshToken;
        set({ accessToken: tokens.accessToken });
      },

      clear: () => {
        _refreshToken = null;
        set({ user: null, accessToken: null });
      },
    }),
    {
      name: "platform-auth",
      partialize: (state) => ({ user: state.user, accessToken: state.accessToken }),
    },
  ),
);
